/**
 * AccessDenied — shown when the signed-in identity authenticated successfully but is NOT a member
 * of this workspace (tenant), or when the profile couldn't be loaded. It deliberately does NOT look
 * like a signed-in app: no sidenav, no dashboard — just a clear message and a way to sign out and
 * use the correct (invited) account. See the server guard in middleware/tenant.js (verifyTenant →
 * 403 not_a_member) and context/UserContext.js (access === "denied").
 */
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useNativeAuth } from "context/NativeAuthContext";
import { useNativeAuthForSignIn } from "authConfig";

function AccessDenied({ reason }) {
  const { instance } = useMsal();
  const { clearTokens, getAccessToken } = useNativeAuth();

  const account = instance.getActiveAccount?.() || null;
  const email =
    account?.username ||
    account?.idTokenClaims?.preferred_username ||
    account?.idTokenClaims?.email ||
    "";
  const workspaceLabel =
    (typeof window !== "undefined" && window.location.hostname) || "this workspace";

  const signOut = async () => {
    try {
      clearTokens?.();
    } catch {
      /* ignore */
    }
    const hadNativeTokens = !!getAccessToken?.();
    if (useNativeAuthForSignIn && hadNativeTokens) {
      window.location.href = "/";
      return;
    }
    const accounts = instance.getAllAccounts?.() || [];
    if (accounts.length > 0) {
      await instance.logoutRedirect();
    } else {
      window.location.href = "/";
    }
  };

  const isError = reason === "error";
  const deniedMessage = email
    ? `You’re signed in as ${email}, which isn’t a member of ${workspaceLabel}.`
    : `This account isn’t a member of ${workspaceLabel}.`;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        backgroundColor: (t) => t.palette.background?.default || "#f0f2f5",
      }}
    >
      <Box
        sx={{
          maxWidth: 460,
          width: "100%",
          textAlign: "center",
          backgroundColor: (t) => t.palette.background?.paper || "#fff",
          borderRadius: 2,
          boxShadow: 3,
          p: 4,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {isError ? "We couldn’t sign you in" : "No access to this workspace"}
        </Typography>

        {isError ? (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            We couldn’t load your profile. Your session may have expired. Please sign out and sign
            in again.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {deniedMessage}
            <br />
            Sign in with the email your invitation was sent to, or ask an administrator to invite
            you.
          </Typography>
        )}

        <Button variant="contained" color="primary" onClick={signOut} sx={{ mt: 1 }}>
          Sign out &amp; use another account
        </Button>
      </Box>
    </Box>
  );
}

AccessDenied.propTypes = {
  reason: PropTypes.string,
};

AccessDenied.defaultProps = {
  reason: "denied",
};

export default AccessDenied;
